/* camera-lag/detect.js — find the button presses and the screen changes in a slow-motion clip.

   Pure functions over low-resolution gray frames (one Uint8Array of N·w·h bytes, frame k at
   k·w·h) and their real capture times (camera-lag/timebase.js). No DOM: the same code runs in
   the page's Worker and in the Node bench (tests/camera-lag/bench-detect.mjs) that proves it
   against composites with KNOWN latency on real 240 fps backgrounds.

   PRESS — a thumb at rest is the picture the box shows most of the time, so the per-pixel
   median of the box over the whole clip is «rest». How far a frame is from rest rises as the
   thumb travels down, holds while the button is held and falls on release. The press is the
   END of the downward stroke — where that distance stops rising (Mick West's «first frame the
   button is fully pressed»). It does not matter which way the thumb moves in the picture.

   SCREEN — before each press the screen box is compared with the median of the frames just
   before it; the first frame after the press that stands above that noise (median + k·MAD of
   the pre-press frames, so game motion and flicker raise the bar themselves) and stays above
   it is the change. Inside that frame the scan-out front (how many of the box's rows already
   changed, and how fast the front moves on the next frame) places the start between frames.

   Everything the page shows comes from analyze(); every number can be overridden by hand
   (the page keeps manual marks and calls stats() again). */
(function (root) {
    'use strict';
    if (root.CameraLagDetect) return;

    function sorted(a) { return Array.prototype.slice.call(a).sort(function (x, y) { return x - y; }); }
    function quantile(s, q) {
        if (!s.length) return NaN;
        var p = (s.length - 1) * q, i = Math.floor(p), f = p - i;
        return i + 1 < s.length ? s[i] * (1 - f) + s[i + 1] * f : s[i];
    }
    function median(a) { return quantile(sorted(a), 0.5); }
    function mad(a, m) {
        if (m == null) m = median(a);
        var d = new Float64Array(a.length);
        for (var i = 0; i < a.length; i++) d[i] = Math.abs(a[i] - m);
        return median(d);
    }
    function clampBox(b, w, h) {
        var x = Math.max(0, Math.min(w - 2, Math.round(b.x))), y = Math.max(0, Math.min(h - 2, Math.round(b.y)));
        var bw = Math.max(2, Math.min(w - x, Math.round(b.w))), bh = Math.max(2, Math.min(h - y, Math.round(b.h)));
        return { x: x, y: y, w: bw, h: bh };
    }

    /* Per-pixel median of a box over the given frame indices (counting sort: values are bytes). */
    function boxMedian(store, box, frames) {
        var w = store.w, fs = store.w * store.h, n = box.w * box.h, ref = new Uint8Array(n);
        var hist = new Uint16Array(256), half = frames.length / 2;
        for (var yy = 0; yy < box.h; yy++) {
            for (var xx = 0; xx < box.w; xx++) {
                hist.fill(0);
                var off = (box.y + yy) * w + box.x + xx;
                for (var k = 0; k < frames.length; k++) hist[store.buf[frames[k] * fs + off]]++;
                var acc = 0, v = 0;
                for (; v < 256; v++) { acc += hist[v]; if (acc >= half) break; }
                ref[yy * box.w + xx] = v;
            }
        }
        return ref;
    }
    /* Mean absolute difference of frame k's box from a reference; optionally per row. */
    function boxDiff(store, box, k, ref, rows) {
        var w = store.w, base = k * store.w * store.h, sum = 0, i = 0;
        for (var yy = 0; yy < box.h; yy++) {
            var rs = 0, off = base + (box.y + yy) * w + box.x;
            for (var xx = 0; xx < box.w; xx++, i++) {
                var d = store.buf[off + xx] - ref[i];
                rs += d < 0 ? -d : d;
            }
            if (rows) rows[yy] = rs / box.w;
            sum += rs;
        }
        return sum / (box.w * box.h);
    }

    /* ── PRESSES ────────────────────────────────────────────────────────────────── */
    function pressSignal(store, box) {
        var N = store.n, step = Math.max(1, Math.floor(N / 480)), idx = [];
        for (var k = 0; k < N; k += step) idx.push(k);
        var ref = boxMedian(store, box, idx);
        var p = new Float64Array(N);
        for (var k2 = 0; k2 < N; k2++) p[k2] = boxDiff(store, box, k2, ref, null);
        p.ref = ref;
        return p;
    }
    /* Second pass. Part of a thumb box is always background — a person walking behind the
       controller, a TV reflection — and in the first pass every pixel votes equally. Once the
       presses are roughly known, each pixel is weighted by ITS OWN signal-to-noise: how much it
       departs from rest while the button is held, against how much it wanders at rest. The
       thumb's edge pixels get almost all the weight, the busy background next to nothing. */
    function weightedPressSignal(store, box, ref, presses, times) {
        var N = store.n, n = box.w * box.h, w = store.w, fs = store.w * store.h;
        var held = new Uint8Array(N), rest = new Uint8Array(N).fill(1);
        presses.forEach(function (pr) {
            for (var k = 0; k < N; k++) {
                var dt = times[k] - pr.tMs;
                if (dt >= 0 && dt <= 50) held[k] = 1;
                if (dt > -250 && dt < 400) rest[k] = 0;
            }
        });
        var sH = new Float64Array(n), cH = 0, sR = new Float64Array(n), qR = new Float64Array(n), cR = 0;
        for (var k2 = 0; k2 < N; k2++) {
            if (!held[k2] && !rest[k2]) continue;
            var i = 0;
            for (var yy = 0; yy < box.h; yy++) {
                var off = k2 * fs + (box.y + yy) * w + box.x;
                for (var xx = 0; xx < box.w; xx++, i++) {
                    var d = store.buf[off + xx] - ref[i]; if (d < 0) d = -d;
                    if (held[k2]) sH[i] += d; else { sR[i] += d; qR[i] += d * d; }
                }
            }
            if (held[k2]) cH++; else cR++;
        }
        if (cH < 2 || cR < 10) return null;
        var wt = new Float64Array(n), wsum = 0;
        for (var j = 0; j < n; j++) {
            var mR = sR[j] / cR, sdR = Math.sqrt(Math.max(0, qR[j] / cR - mR * mR));
            var gain = sH[j] / cH - mR;
            wt[j] = gain > 0 ? gain / (sdR + 1) : 0; wsum += wt[j];
        }
        if (wsum <= 0) return null;
        var p = new Float64Array(N);
        for (var k3 = 0; k3 < N; k3++) {
            var acc = 0, i2 = 0;
            for (var y2 = 0; y2 < box.h; y2++) {
                var o2 = k3 * fs + (box.y + y2) * w + box.x;
                for (var x2 = 0; x2 < box.w; x2++, i2++) { if (!wt[i2]) continue; var d2 = store.buf[o2 + x2] - ref[i2]; acc += wt[i2] * (d2 < 0 ? -d2 : d2); }
            }
            p[k3] = acc / wsum;
        }
        return p;
    }
    /* Otsu split of a 1-D sample: the threshold with the largest between-class variance. */
    function otsu(vals, lo, hi) {
        var B = 128, h = new Float64Array(B), n = 0;
        for (var i = 0; i < vals.length; i++) { var b = Math.floor((vals[i] - lo) / (hi - lo + 1e-9) * B); if (b < 0 || b >= B) continue; h[b]++; n++; }
        var sumAll = 0; for (var j = 0; j < B; j++) sumAll += j * h[j];
        var wB = 0, sB = 0, best = -1, bt = B / 2;
        for (var t = 0; t < B; t++) {
            wB += h[t]; if (!wB) continue; var wF = n - wB; if (!wF) break;
            sB += t * h[t]; var mB = sB / wB, mF = (sumAll - sB) / wF, v = wB * wF * (mB - mF) * (mB - mF);
            if (v > best) { best = v; bt = t; }
        }
        // how bimodal the sample is: between-class over total variance (a single Gaussian gives 0.64)
        var mu = sumAll / Math.max(1, n), tot = 0;
        for (var u = 0; u < B; u++) tot += h[u] * (u - mu) * (u - mu);
        otsu.eta = tot > 0 ? (best / n) / tot : 0;
        return lo + (bt + 1) / B * (hi - lo);
    }
    function findPresses(p, times, opts) {
        opts = opts || {};
        var N = p.length, s = sorted(p);
        /* Two populations: the thumb at rest (most frames) and the thumb held down. A noise rule
           («6 sigma above the median») sank every press on a busy background, where the rest
           level itself wanders as much as a press lifts it; the split between the two
           populations does not depend on how noisy the rest is. */
        var lo0 = quantile(s, 0.01), hi0 = quantile(s, 0.995);
        var cut = otsu(p, lo0, hi0), restV = [], heldV = [];
        for (var z = 0; z < N; z++) (p[z] < cut ? restV : heldV).push(p[z]);
        var base = restV.length ? median(restV) : quantile(s, 0.5), top = heldV.length ? median(heldV) : quantile(s, 0.985);
        var spread = restV.length ? mad(restV, base) * 1.4826 : 0;
        var eta = otsu.eta, sep = (top - base) / Math.max(spread, 0.25);
        var flat = eta < 0.75 || sep < 4 || (eta < 0.8 && sep < 8);
        var thrHi = Math.max(base + 0.55 * (top - base), base + 3 * spread, base + 1.2);
        var thrLo = Math.max(base + 0.3 * (top - base), base + 1.5 * spread, base + 0.6);
        var minGap = opts.minGapMs || 220, rule = opts.pressRule || 'knee';
        var segs = [], k = 0;
        while (k < N) {
            if (p[k] > thrHi) {
                var a = k; while (a > 0 && p[a - 1] > thrLo) a--;
                var b = k; while (b < N - 1 && p[b + 1] > thrLo) b++;
                segs.push([a, b]); k = b + 1;
            } else k++;
        }
        // a release bounce or a flicker blip right after a press is not a second press
        var merged = [];
        for (var i = 0; i < segs.length; i++) {
            var last = merged[merged.length - 1];
            if (last && times[segs[i][0]] - times[last[1]] < 60) last[1] = segs[i][1];
            else merged.push(segs[i].slice());
        }
        var presses = [];
        for (var j = 0; j < merged.length; j++) {
            var s0 = merged[j][0], e0 = merged[j][1];
            if (e0 - s0 < 1) continue;
            // the stroke: from the rise start to the first local plateau inside 300 ms
            var limT = times[s0] + 300, pk = s0;
            for (var q = s0; q <= e0 && times[q] <= limT; q++) if (p[q] > p[pk]) pk = q;
            var lvl = p[pk] - base, t;
            if (rule === 'level') {
                var target = base + 0.9 * lvl, q2 = s0;
                while (q2 < pk && p[q2] < target) q2++;
                t = q2 > s0 && p[q2] !== p[q2 - 1] ? times[q2 - 1] + (target - p[q2 - 1]) / (p[q2] - p[q2 - 1]) * (times[q2] - times[q2 - 1]) : times[q2];
            } else {
                // knee: where the rise slows below 20 % of its fastest rate (velocity → 0)
                var vmax = 0, vi = s0;
                for (var q3 = s0 + 1; q3 <= pk; q3++) {
                    var v = (p[q3] - p[q3 - 1]) / Math.max(1e-6, times[q3] - times[q3 - 1]);
                    if (v > vmax) { vmax = v; vi = q3; }
                }
                var q4 = vi;
                while (q4 < pk) {
                    var v2 = (p[q4 + 1] - p[q4]) / Math.max(1e-6, times[q4 + 1] - times[q4]);
                    if (v2 < 0.2 * vmax || p[q4] >= base + 0.97 * lvl) break;
                    q4++;
                }
                t = times[q4];
            }
            if (presses.length && t - presses[presses.length - 1].tMs < minGap) continue;
            /* How long the stroke took, in FRAMES: from the first frame 10 % of the way down to the
               press. A thumb needs 30–100 ms to push a button, so a true 240 fps clip shows it over
               8–25 frames and a 30 fps one over one or two. That is the check on a clock the person
               chose (judge(): 'clock-fast' / 'clock-slow'). */
            var r0 = s0; while (r0 > 0 && p[r0 - 1] > base + 0.1 * lvl) r0--;
            var kp = nearestFrame(times, t);
            presses.push({ tMs: t, frame: kp, seg: [s0, e0], peak: pk, level: lvl, riseFrames: Math.max(0, kp - r0), holdFrames: e0 - kp });
        }
        return { presses: presses, base: base, thrHi: thrHi, thrLo: thrLo, sep: sep, eta: eta, flat: flat };
    }
    function nearestFrame(times, t) {
        var lo = 0, hi = times.length - 1;
        while (lo < hi) { var m = (lo + hi) >> 1; if (times[m] < t) lo = m + 1; else hi = m; }
        if (lo > 0 && Math.abs(times[lo - 1] - t) < Math.abs(times[lo] - t)) lo--;
        return lo;
    }

    /* ── SCREEN ONSET after one press ──────────────────────────────────────────── */
    function onsetAfter(store, box, times, pressMs, untilMs, opts) {
        opts = opts || {};
        var preMs = opts.preMs || 140, k = opts.k || 6;
        var pre = [];
        for (var i = 0; i < times.length; i++) {
            if (times[i] >= pressMs - preMs && times[i] <= pressMs - 3) pre.push(i);
            if (times[i] > pressMs) break;
        }
        if (pre.length < 3) return { found: false, why: 'no-pre' };
        var ref = boxMedian(store, box, pre);
        var dpre = [], rowsPre = [], rbuf = new Float64Array(box.h);
        for (var a = 0; a < pre.length; a++) {
            dpre.push(boxDiff(store, box, pre[a], ref, rbuf));
            rowsPre.push(Float64Array.from(rbuf));
        }
        var base = median(dpre), sp = mad(dpre, base) * 1.4826;
        var thr = base + Math.max(k * sp, opts.minAbs || 1.6);
        // per-row thresholds for the scan-out front
        var rowThr = new Float64Array(box.h);
        for (var r = 0; r < box.h; r++) {
            var col = rowsPre.map(function (x) { return x[r]; });
            var mb = median(col);
            rowThr[r] = mb + Math.max(k * mad(col, mb) * 1.4826, 2.5);
        }
        var first = -1, end = times.length - 1;
        for (var f = pre[pre.length - 1] + 1; f < times.length; f++) { if (times[f] > pressMs) { first = f; break; } }
        if (first < 0) return { found: false, why: 'no-after' };
        while (end > first && times[end] > untilMs) end--;
        var D = [], rows = [];
        for (var g = first; g <= Math.min(end + 8, times.length - 1); g++) {
            D.push(boxDiff(store, box, g, ref, rbuf));
            var ch = 0; for (var r2 = 0; r2 < box.h; r2++) if (rbuf[r2] > rowThr[r2]) ch++;
            rows.push(ch / box.h);
        }
        /* The change is the LARGEST sustained step in the window, not the first frame above
           noise: a 30 fps game moves its picture every 8 camera frames, and the first such move
           after a press beat the response to the threshold (bench S3: p95 error 27 ms). Steps
           of at least 60 % of the largest count, the earliest of them wins; its start is where
           the box first rises a quarter of the way from before to after. */
        var med4 = function (arr, i, j) { var x = arr.slice(Math.max(0, i), Math.max(Math.max(0, i) + 1, j)); return x.length ? median(x) : base; };
        var lastU = Math.min(D.length - 2, end - first), scores = [], best = 0;
        for (var u = 0; u <= lastU; u++) {
            var sc = (D[u] > thr && D[u + 1] > thr) ? med4(D, u, u + 4) - (u >= 3 ? med4(D, u - 3, u) : base) : 0;
            scores.push(sc); if (sc > best) best = sc;
        }
        var bu = -1;
        for (var u2 = 0; u2 < scores.length; u2++) if (best > 0 && scores[u2] >= 0.6 * best) { bu = u2; break; }
        if (bu < 0) return { found: false, why: 'no-change', thr: thr, base: base };
        var after = med4(D, bu + 2, bu + 9), before = bu >= 4 ? med4(D, bu - 4, bu) : base;
        if (after - Math.min(before, base) < Math.max(4 * sp, 1.2)) return { found: false, why: 'no-change', thr: thr, base: base };
        /* The rise, measured as a share of the step itself (0 = the level before, 1 = after),
           not against the noise bar: on a moving game the noise bar sits at a third of the step,
           and the first frame of the scan-out (30 % of the box already new) fell under it — every
           onset came one frame late (bench S1: +4.2 ms). */
        var span = after - before, frac = function (u3) { return (D[u3] - before) / span; };
        var lo = bu; while (lo > 0 && frac(lo - 1) >= 0.1 && D[lo - 1] <= D[lo] + 0.5 * sp) lo--;
        while (lo < bu && frac(lo) < 0.1) lo++;
        var on = lo, kf = first + on, tk = times[kf], tp = kf > 0 ? times[kf - 1] : tk - (times[kf + 1] - tk);
        /* Between frames: the box fills as the display scans it (and an LCD fades), so the share
           rises roughly linearly. A line through the frames of the rise (10–90 %) followed back to
           the level before says when it started; one frame in the rise — the share of it tells
           how far into the previous frame gap the start was; none — the middle of that gap. */
        var tau;
        if (opts.subframe === false) tau = tk;
        else {
            var xs = [], ys = [];
            for (var u4 = on; u4 < D.length && first + u4 < times.length; u4++) {
                var fr = frac(u4); if (fr > 0.9) break;
                xs.push(times[first + u4]); ys.push(fr);
            }
            if (xs.length >= 2) {
                var mx = 0, my = 0; for (var i5 = 0; i5 < xs.length; i5++) { mx += xs[i5]; my += ys[i5]; } mx /= xs.length; my /= xs.length;
                var sxy = 0, sxx = 0; for (var i6 = 0; i6 < xs.length; i6++) { sxy += (xs[i6] - mx) * (ys[i6] - my); sxx += (xs[i6] - mx) * (xs[i6] - mx); }
                var sl = sxx > 0 ? sxy / sxx : 0;
                tau = sl > 0 ? mx - my / sl : tk - (tk - tp) / 2;
            } else if (xs.length === 1) tau = tk - Math.min(1, Math.max(0, ys[0])) * (tk - tp);
            else tau = tk - (tk - tp) / 2;
            tau = Math.max(tp, Math.min(tk, tau));
        }
        var phiK = Math.min(1, Math.max(0, frac(on)));
        return { found: true, frame: kf, tMs: tau, frameMs: tk, thr: thr, base: base, phi: phiK };
    }

    /* ── WHOLE RUN ─────────────────────────────────────────────────────────────── */
    function analyze(store, times, boxes, opts) {
        opts = opts || {};
        var tb = clampBox(boxes.thumb, store.w, store.h), sb = clampBox(boxes.screen, store.w, store.h);
        var p = pressSignal(store, tb);
        var fp = findPresses(p, times, opts);
        if (opts.weighted !== false && fp.presses.length >= 3) {
            var pw = weightedPressSignal(store, tb, p.ref, fp.presses, times);
            if (pw) {
                var fw = findPresses(pw, times, opts);
                if (fw.presses.length >= Math.min(3, fp.presses.length)) { p = pw; fp = fw; }
            }
        }
        /* Otsu splits ANY sample in two, so a hand that only trembles also gets «presses» — 23 of
           them on a clip without a single one (QA G09). Real presses make the FINAL signal (after
           the per-pixel weighting) two clear populations: between-class share 0.81–0.96 and the
           held level 5–300 rest-spreads above rest on every clip with presses; tremor alone gave
           0.65 and 2 (a single Gaussian is 0.64), a slow 1-px wander of a soft thumb edge 0.76 and
           7 (tests/js/camera-lag-judge.test.mjs). Below the bars there is nothing to find.
           (The first, unweighted pass may sit lower on a busy background — 3.2–3.8 on QA G01,
           G02b, G16 — which is why the bar is applied here, not inside findPresses.) */
        if (fp.flat && opts.anySplit !== true) fp = { presses: [], thrLo: fp.thrLo, thrHi: fp.thrHi, sep: fp.sep, eta: fp.eta, rejected: 'flat' };
        var maxLag = opts.maxLagMs || 700;
        /* Rolling shutter: a phone reads each frame top to bottom, so a row lower in the picture
           is seen later than the frame's timestamp (the first row). The thumb is judged at its
           box's middle, the screen at its box's top (where the change starts); readout is taken
           as 0.85 of a frame — at 240 fps it must fit inside 4.2 ms. */
        var N0 = times.length, frameMs = N0 > 1 ? (times[N0 - 1] - times[0]) / (N0 - 1) : 4.17;
        var readout = opts.readoutMs != null ? opts.readoutMs : Math.min(0.85 * frameMs, 30);
        /* The sensor reads ITS rows, and a portrait clip is a landscape sensor turned on its side:
           with a display rotation the rows run across the picture, not down it (G05, 90°:
           −4.2 ms on every press while the shift was taken by height). */
        var rot = opts.rotationDeg || 0;
        var shiftPress = rowFrac((tb.x + tb.w / 2) / store.w, (tb.y + tb.h / 2) / store.h, rot) * readout;
        var shiftScreen = rowFrac((sb.x + sb.w / 2) / store.w, sb.y / store.h, rot) * readout;
        fp.presses.forEach(function (pr) { pr.tMs += shiftPress; });
        var pairs = fp.presses.map(function (pr, i) {
            var next = i + 1 < fp.presses.length ? fp.presses[i + 1].tMs : Infinity;
            var until = Math.min(pr.tMs + maxLag, next - 5);
            var on = onsetAfter(store, sb, times, pr.tMs, until, opts);
            if (on.found) on.tMs += shiftScreen;
            return { press: pr, onset: on.found ? on : null, why: on.found ? null : on.why,
                     latencyMs: on.found ? on.tMs - pr.tMs : null, excluded: false };
        });
        var lat = pairs.filter(function (x) { return x.latencyMs != null; }).map(function (x) { return x.latencyMs; });
        return { pressSignal: p, pressThr: [fp.thrLo, fp.thrHi], pairs: pairs, stats: stats(lat),
                 sep: fp.sep, eta: fp.eta, rejected: fp.rejected || null,
                 cadence: cadence(store, sb, times, fp.presses), boxes: { thumb: tb, screen: sb } };
    }

    /* Which share of the frame read-out a point of the PICTURE (fractions of the displayed width
       and height) is read at, for a clip shown turned by `rot` degrees clockwise (the tkhd
       convention, VideoOrientation.fromTkhd). */
    function rowFrac(x, y, rot) {
        return rot === 90 ? 1 - x : rot === 270 ? x : rot === 180 ? 1 - y : y;
    }

    function stats(lat) {
        var s = sorted(lat.filter(function (x) { return isFinite(x); }));
        if (!s.length) return { n: 0 };
        var m = quantile(s, 0.5), md = mad(s, m) * 1.4826;
        return { n: s.length, median: m, q1: quantile(s, 0.25), q3: quantile(s, 0.75), min: s[0], max: s[s.length - 1],
                 mean: s.reduce(function (a, b) { return a + b; }, 0) / s.length, mad: md,
                 outliers: s.filter(function (x) { return md > 0 && Math.abs(x - m) > 3.5 * md; }) };
    }

    /* Bootstrap CI of the difference of medians (b − a). Seeded so the page and its PDF agree. */
    function bootstrapDiff(a, b, iters, seed) {
        iters = iters || 4000;
        var x = seed || 1234567;
        function rnd() { x ^= x << 13; x >>>= 0; x ^= x >>> 17; x ^= x << 5; x >>>= 0; return x / 4294967296; }
        function med(arr) { return quantile(sorted(arr), 0.5); }
        var ds = new Float64Array(iters), ra = new Array(a.length), rb = new Array(b.length);
        for (var it = 0; it < iters; it++) {
            for (var i = 0; i < a.length; i++) ra[i] = a[Math.floor(rnd() * a.length)];
            for (var j = 0; j < b.length; j++) rb[j] = b[Math.floor(rnd() * b.length)];
            ds[it] = med(rb) - med(ra);
        }
        var s = sorted(ds);
        return { diff: med(b) - med(a), lo: quantile(s, 0.025), hi: quantile(s, 0.975) };
    }

    /* Game frame rate from the screen box between presses. A display scans top→bottom, so at
       240 fps one 60 Hz refresh lands on ~4 camera frames, a quarter of the box each: the whole
       box changes a little every frame and shows no rhythm. A horizontal BAND of rows does —
       it is rewritten once per game frame. Autocorrelation of each band's frame-to-frame change
       (press windows cut out), period = the lag most bands agree on. */
    function cadence(store, box, times, presses) {
        var N = store.n; if (N < 60) return null;
        var w = store.w, fs = store.w * store.h, B = Math.max(2, Math.min(8, Math.floor(box.h / 3)));
        var keep = new Uint8Array(N).fill(1); keep[0] = 0;
        (presses || []).forEach(function (pr) {
            for (var k2 = 0; k2 < N; k2++) if (times[k2] > pr.tMs - 30 && times[k2] < pr.tMs + 700) keep[k2] = 0;
        });
        var votes = {}, strength = [];
        for (var bi = 0; bi < B; bi++) {
            var y0 = box.y + Math.floor(bi * box.h / B), y1 = box.y + Math.floor((bi + 1) * box.h / B);
            var d = new Float64Array(N);
            for (var k = 1; k < N; k++) {
                var s = 0;
                for (var yy = y0; yy < y1; yy++) {
                    var o1 = k * fs + yy * w + box.x, o0 = o1 - fs;
                    for (var xx = 0; xx < box.w; xx++) { var q = store.buf[o1 + xx] - store.buf[o0 + xx]; s += q < 0 ? -q : q; }
                }
                d[k] = s / ((y1 - y0) * box.w);
            }
            var idx = []; for (var k3 = 1; k3 < N; k3++) if (keep[k3] && keep[k3 - 1]) idx.push(k3);
            if (idx.length < 40) continue;
            var mean = 0; idx.forEach(function (i) { mean += d[i]; }); mean /= idx.length;
            var v0 = 0; idx.forEach(function (i) { v0 += (d[i] - mean) * (d[i] - mean); });
            if (v0 <= 1e-9) continue;
            var best = 0, bestLag = 0, rs = {};
            for (var lag = 2; lag <= 20; lag++) {
                var c = 0, n2 = 0;
                for (var j = 0; j < idx.length; j++) {
                    var i2 = idx[j] + lag; if (i2 >= N || !keep[i2]) continue;
                    c += (d[idx[j]] - mean) * (d[i2] - mean); n2++;
                }
                if (n2 < 20) continue;
                var r = (c / n2) / (v0 / idx.length);
                rs[lag] = r;
                if (r > best) best = r;
            }
            // the FUNDAMENTAL period: the smallest lag that is nearly as strong as the best one
            // (a 60 fps rhythm also correlates at 8 and 12 frames)
            for (var lag2 = 2; lag2 <= 20; lag2++) if (rs[lag2] != null && rs[lag2] >= 0.85 * best) { bestLag = lag2; break; }
            if (best > 0.25 && bestLag) { votes[bestLag] = (votes[bestLag] || 0) + 1; strength.push(best); }
        }
        var lags = Object.keys(votes).map(Number);
        if (!lags.length) return { reliable: false, why: 'no-rhythm' };
        lags.sort(function (a2, b2) { return votes[b2] - votes[a2]; });
        var P = lags[0], share = votes[P] / B;
        var frameMs = (times[N - 1] - times[0]) / (N - 1);
        return { reliable: share >= 0.5, periodFrames: P, gameFps: 1000 / (P * frameMs), share: share, strength: median(strength) };
    }

    /* ── Box suggestion ───────────────────────────────────────────────────────────
       The screen's response to a press is the BIGGEST change in the picture — larger than a
       game's ordinary frame-to-frame motion, larger than a person walking by. So: peaks of the
       whole frame's change energy are the screen events; the cells that jump at those peaks are
       the screen; the cells OUTSIDE it that move in the 15–450 ms before each event and are quiet
       otherwise are the thumb. Motion is read over ~25 ms for the thumb: at 240 fps a thumb moves
       a fraction of a pixel per frame, so a neighbour difference does not see it at all.
       The page checks whatever comes out by running the analysis (judge()). */
    function cellActivity(store, times, lagMs) {
        var w = store.w, h = store.h, N = store.n, fs = w * h, C = 4;
        var dt = N > 1 ? (times[N - 1] - times[0]) / (N - 1) : 4.17, L = lagMs ? Math.max(1, Math.round(lagMs / dt)) : 1;
        var gw = Math.floor(w / C), gh = Math.floor(h / C), G = gw * gh, act = new Float32Array(G * N);
        for (var k = L; k < N; k++) {
            for (var cy = 0; cy < gh; cy++) for (var cx = 0; cx < gw; cx++) {
                var s2 = 0;
                for (var yy = 0; yy < C; yy++) {
                    var o1 = k * fs + (cy * C + yy) * w + cx * C, o0 = o1 - L * fs;
                    for (var xx = 0; xx < C; xx++) { var q = store.buf[o1 + xx] - store.buf[o0 + xx]; s2 += q < 0 ? -q : q; }
                }
                act[(cy * gw + cx) * N + k] = s2 / (C * C);
            }
        }
        return { act: act, gw: gw, gh: gh, G: G, C: C, N: N, L: L };
    }
    function spikesOf(A, k0) {
        var G = A.G, N = A.N, sp = new Uint8Array(G * N), cnt = new Uint32Array(G);
        for (var c = 0; c < G; c++) {
            var row = A.act.subarray(c * N + k0, c * N + N), m = median(row), md = mad(row, m) * 1.4826;
            var thr = m + Math.max(6 * md, 3);
            for (var k = k0; k < N; k++) if (A.act[c * N + k] > thr) { sp[c * N + k] = 1; cnt[c]++; }
        }
        return { sp: sp, cnt: cnt };
    }
    function suggestBoxes(store, times) {
        var N = store.n; if (N < 30) return null;
        var A1 = cellActivity(store, times, 0), G = A1.G, gw = A1.gw, gh = A1.gh, C = A1.C;
        var S1 = spikesOf(A1, 1);
        // whole-frame change energy and its peaks
        var E = new Float64Array(N);
        for (var k = 1; k < N; k++) { var e = 0; for (var c = 0; c < G; c++) e += A1.act[c * N + k]; E[k] = e / G; }
        var Es = sorted(Array.prototype.slice.call(E, 1)), eMed = quantile(Es, 0.5), eTop = quantile(Es, 0.995);
        var eThr = eMed + 0.4 * (eTop - eMed), evs = [];
        for (var k2 = 1; k2 < N; k2++) {
            if (E[k2] < eThr) continue;
            var isPeak = true;
            for (var q = k2 - 1; q >= 1 && times[k2] - times[q] < 150; q--) if (E[q] > E[k2]) { isPeak = false; break; }
            for (var q2 = k2 + 1; isPeak && q2 < N && times[q2] - times[k2] < 150; q2++) if (E[q2] >= E[k2]) { isPeak = false; break; }
            if (isPeak) evs.push(k2);
        }
        if (evs.length < 3) return { confidence: 0, events: evs.length };
        // the screen: cells that jump at the events
        var smask = new Uint8Array(G);
        for (var c2 = 0; c2 < G; c2++) {
            var hits = 0;
            evs.forEach(function (ev) { for (var d = -2; d <= 2; d++) { var kk = ev + d; if (kk > 0 && kk < N && S1.sp[c2 * N + kk]) { hits++; break; } } });
            smask[c2] = hits >= 0.5 * evs.length ? 1 : 0;
        }
        var scr = largestComponent(smask, gw, gh);
        if (!scr) return { confidence: 0, events: evs.length };
        // the thumb: slow (25 ms) motion just before each event, quiet otherwise, outside the screen
        var AL = cellActivity(store, times, 25), SL = spikesOf(AL, AL.L);
        var tmask = new Uint8Array(G);
        for (var c3 = 0; c3 < G; c3++) {
            var cx = c3 % gw, cy = (c3 / gw) | 0;
            if (cx >= scr.x0 - 1 && cx <= scr.x1 + 1 && cy >= scr.y0 - 1 && cy <= scr.y1 + 1) continue;
            var lead = 0;
            evs.forEach(function (ev) {
                for (var k5 = ev - 1; k5 > 0 && times[ev] - times[k5] < 450; k5--) {
                    if (times[ev] - times[k5] < 15) continue;
                    if (SL.sp[c3 * N + k5]) { lead++; break; }
                }
            });
            var busy = SL.cnt[c3] / N;
            tmask[c3] = lead >= 0.6 * evs.length && busy < 0.3 ? 1 : 0;
        }
        var th = largestComponent(tmask, gw, gh);
        var w = store.w, h = store.h;
        function toBox(cc, pad) {
            return clampBox({ x: (cc.x0 - pad) * C, y: (cc.y0 - pad) * C, w: (cc.x1 - cc.x0 + 1 + 2 * pad) * C, h: (cc.y1 - cc.y0 + 1 + 2 * pad) * C }, w, h);
        }
        var screen = toBox(scr, 0);
        screen = clampBox({ x: screen.x + screen.w * 0.08, y: screen.y + screen.h * 0.08, w: screen.w * 0.84, h: screen.h * 0.84 }, w, h);
        return { screen: screen, thumb: th ? toBox(th, 1) : null, events: evs.length,
                 confidence: th ? Math.min(1, evs.length / 6) : 0.3 };
    }

    /* What may the page show for an analysis? ONE answer for every source of boxes:
         state  'ok'          — a number (provisional: fewer than five latencies — shown, marked)
                'no-presses'  — nothing in the thumb box moves like a press
                'no-change'   — presses, but the screen box does not change after most of them
                'scattered'   — presses and changes do not belong together (spread wider than any
                                display chain: the screen box watches something else)
                'implausible' — a median no display chain gives
                'clock-fast'  — the strokes are 1–2 frames long: the clip is not slow motion at
                                the clock in use (a 30/60 fps file taken as 240: «4 ms»)
                'clock-slow'  — the person said «normal video», but a stroke would take > 200 ms
                'find-boxes'  — automatic boxes did not pass the strict check
       ctx = { source: 'auto' | 'user', boxes, frameMs, clockAsked }
       Automatic boxes pass only a STRICT check — a wrong pair of boxes still yields «presses»
       and «changes» (QA G16: 5 presses → 514 ms; G17: 222 ms), and a confident wrong number is
       worse than asking for two taps. The person's own boxes get a number as soon as the
       evidence holds together; the reason is said whenever it does not. */
    function judge(result, ctx) {
        ctx = ctx || {};
        var out = { state: 'ok', why: null, show: false, provisional: false, median: null, n: 0, presses: 0 };
        if (!result || !result.pairs) { out.state = 'no-presses'; return out; }
        var pairs = result.pairs, n = pairs.length; out.presses = n;
        var lat = pairs.filter(function (p) { return p.latencyMs != null; }).map(function (p) { return p.latencyMs; });
        var st = stats(lat); out.n = st.n; out.median = st.n ? st.median : null;
        var frameMs = ctx.frameMs || 4.17, auto = ctx.source === 'auto';
        function no(state, why) { out.state = state; out.why = why || state; out.show = false; return out; }
        if (!n) return no(auto ? 'find-boxes' : 'no-presses', result.rejected || 'no-presses');
        // the stroke length under this clock — only when the clock is the person's word or a guess
        var rise = median(pairs.map(function (p) { return p.press.riseFrames || 0; }));
        out.riseMs = rise * frameMs;
        if (ctx.clockAsked && n >= 3) {
            if (out.riseMs < 13 && frameMs < 10) return no('clock-fast', 'stroke ' + Math.round(out.riseMs) + ' ms');
            if (out.riseMs > 200 && frameMs >= 10) return no('clock-slow', 'stroke ' + Math.round(out.riseMs) + ' ms');
        }
        if (!st.n) return no(auto ? 'find-boxes' : 'no-change', 'no-change');
        var iqr = st.q3 - st.q1, gaps = [];
        for (var i = 1; i < n; i++) gaps.push(pairs[i].press.tMs - pairs[i - 1].press.tMs);
        var mg = gaps.length ? median(gaps) : 1000;
        if (auto) {
            var b = ctx.boxes;
            if (n < 5) return no('find-boxes', 'few-presses');
            if (st.n < 0.8 * n) return no('find-boxes', 'few-changes');
            if (st.median < 15 || st.median > 450 || st.min < 5) return no('find-boxes', 'implausible-lag');
            if (iqr > Math.max(0.5 * st.median, 40)) return no('find-boxes', 'scattered');
            if (mg < 250 || mg > 4000) return no('find-boxes', 'rhythm');
            if (b && b.thumb && b.screen && b.thumb.w * b.thumb.h > b.screen.w * b.screen.h) return no('find-boxes', 'roles');
        } else {
            if (n >= 4 && st.n < 0.5 * n) return no('no-change', 'few-changes');
            if (st.median < Math.max(5, frameMs) || st.median > 700) return no('implausible', 'median ' + Math.round(st.median));
            if (st.n >= 5 && iqr > Math.max(0.6 * st.median, 60)) return no('scattered', 'iqr ' + Math.round(iqr));
        }
        out.show = true; out.provisional = st.n < 5;
        return out;
    }

    function largestComponent(mask, gw, gh) {
        var seen = new Uint8Array(mask.length), best = null;
        for (var i = 0; i < mask.length; i++) {
            if (!mask[i] || seen[i]) continue;
            var st = [i], n = 0, x0 = 1e9, y0 = 1e9, x1 = -1, y1 = -1; seen[i] = 1;
            while (st.length) {
                var c = st.pop(), x = c % gw, y = (c / gw) | 0; n++;
                if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
                for (var dy = -1; dy <= 1; dy++) for (var dx = -1; dx <= 1; dx++) {
                    var nx = x + dx, ny = y + dy;
                    if (nx < 0 || ny < 0 || nx >= gw || ny >= gh) continue;
                    var j = ny * gw + nx; if (mask[j] && !seen[j]) { seen[j] = 1; st.push(j); }
                }
            }
            if (!best || n > best.n) best = { n: n, x0: x0, y0: y0, x1: x1, y1: y1 };
        }
        return best && best.n >= 2 ? best : null;
    }

    /* Exact repeats of the previous frame: a render that duplicated frames breaks the index clock. */
    function duplicateShare(store) {
        var N = store.n, fs = store.w * store.h, dup = 0;
        for (var k = 1; k < N; k++) {
            var s = 0, o1 = k * fs, o0 = o1 - fs;
            for (var i = 0; i < fs; i += 3) { var q = store.buf[o1 + i] - store.buf[o0 + i]; s += q < 0 ? -q : q; }
            if (s / (fs / 3) < 0.15) dup++;
        }
        return dup / Math.max(1, N - 1);
    }

    root.CameraLagDetect = {
        analyze: analyze, pressSignal: pressSignal, findPresses: findPresses, onsetAfter: onsetAfter,
        stats: stats, bootstrapDiff: bootstrapDiff, cadence: cadence,
        suggestBoxes: suggestBoxes, judge: judge, rowFrac: rowFrac, duplicateShare: duplicateShare, clampBox: clampBox, nearestFrame: nearestFrame,
        _median: median, _quantile: quantile
    };
})(typeof self !== 'undefined' ? self : this);
